import * as THREE from 'three';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import { GameSimulation, WORLD_RADIUS } from '../simulation/game';
import type { AircraftState, SimEvent } from '../simulation/types';
import { forwardOf, lookRotation, randomBetween, WORLD_UP } from '../utils/math';

interface MissileRenderable {
  id: string;
  faction: 'player' | 'enemy';
  position: THREE.Vector3;
  direction: THREE.Vector3;
  speed?: number;
  age?: number;
}

interface AircraftVisual {
  root: THREE.Group;
  aircraftId: string;
  modelScale: number;
  engine: THREE.Mesh;
  afterburnerLeftCore: THREE.Sprite;
  afterburnerRightCore: THREE.Sprite;
  afterburnerLeftGlow: THREE.Sprite;
  afterburnerRightGlow: THREE.Sprite;
  smokePrimary: THREE.Sprite;
  smokeSecondary: THREE.Sprite;
  healthBack: THREE.Mesh;
  healthFill: THREE.Mesh;
  vaporLeft: THREE.Sprite;
  vaporRight: THREE.Sprite;
  lastForward: THREE.Vector3;
  vaporStrength: number;
}

interface MissileVisual {
  root: THREE.Group;
  line: THREE.Line;
  points: THREE.Vector3[];
  flame: THREE.Mesh;
  displayPosition: THREE.Vector3;
  displayDirection: THREE.Vector3;
  targetPosition: THREE.Vector3;
  targetDirection: THREE.Vector3;
  speed: number;
  smooth: boolean;
  age: number;
}

interface FlareVisual {
  mesh: THREE.Mesh;
  light: THREE.PointLight;
}

interface ExplosionVisual {
  mesh: THREE.Mesh;
  age: number;
  life: number;
  maxRadius: number;
}

interface TracerVisual {
  line: THREE.Line;
  age: number;
  life: number;
}

interface ScreenPoint {
  x: number;
  y: number;
  visible: boolean;
}

export class AirCombatRenderer {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(62, 1, 0.1, 18000);
  readonly renderer: THREE.WebGLRenderer;
  private cameraOrbitYaw = 0;
  private cameraOrbitPitch = 0.14;
  private cameraZoomOffset = 0;
  private cameraZoomTarget = 0;
  private autoTrackTarget: THREE.Vector3 | null = null;

  private aircraftTemplate: THREE.Object3D | null = null;
  private readonly aircraftTemplates = new Map<string, THREE.Object3D>();
  private selectedAircraftId = 'j15';
  private readonly aircraft = new Map<string, AircraftVisual>();
  private readonly missiles = new Map<string, MissileVisual>();
  private readonly remoteMissiles = new Map<string, MissileVisual>();
  private readonly flares = new Map<string, FlareVisual>();
  private readonly remotePlayers = new Map<string, {
    root: THREE.Group;
    modelScale: number;
    engine: THREE.Mesh;
    afterburnerLeftCore: THREE.Sprite;
    afterburnerRightCore: THREE.Sprite;
    afterburnerLeftGlow: THREE.Sprite;
    afterburnerRightGlow: THREE.Sprite;
    label: THREE.Sprite;
  }>();
  private readonly explosions: ExplosionVisual[] = [];
  private readonly tracers: TracerVisual[] = [];
  private readonly gltfLoader = new GLTFLoader();
  private readonly fbxLoader = new FBXLoader();
  private flameTexture: THREE.CanvasTexture | null = null;
  private smokeTexture: THREE.CanvasTexture | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private carrierGroup: THREE.Group | null = null;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly simulation: GameSimulation,
  ) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  }

  async init(): Promise<void> {
    this.setupScene();
    void this.loadAircraftTemplate('j15');
    void Promise.all([
      this.loadAircraftTemplate('j15'),
      this.loadAircraftTemplate('fa18f'),
    ]).then(() => this.decorateCarrierDeck()).catch(() => {});
    this.resize();
    window.addEventListener('resize', this.resize);
    this.resizeObserver = new ResizeObserver(this.resize);
    this.resizeObserver.observe(document.body);
  }

  dispose(): void {
    window.removeEventListener('resize', this.resize);
    this.resizeObserver?.disconnect();
    this.renderer.dispose();
  }

  render(dt: number, events: SimEvent[]): void {
    this.applyEvents(events);
    this.syncAircraft();
    this.syncMissiles();
    this.updateRemoteMissiles(dt);
    this.syncFlares();
    this.updateExplosions(dt);
    this.updateTracers(dt);
    this.updateCamera(dt);
    this.renderer.render(this.scene, this.camera);
  }

  async setPlayerAircraft(aircraftId: string): Promise<void> {
    this.selectedAircraftId = aircraftId;
    this.aircraftTemplate = await this.loadAircraftTemplate(aircraftId);
    const current = this.aircraft.get(this.simulation.player.id);
    if (current) {
      this.scene.remove(current.root);
      this.aircraft.delete(this.simulation.player.id);
    }
  }

  syncRemotePlayers(players: Array<{ id: string; name: string; position: THREE.Vector3; quaternion: THREE.Quaternion; throttle: number }>): void {
    const active = new Set<string>();
    for (const player of players) {
      active.add(player.id);
      if (!this.remotePlayers.has(player.id)) {
        const root = new THREE.Group();
        const model = this.createAircraftModel('player', 'j15');
        const tint = new THREE.Color(0x7dffa8);
        model.traverse((child) => {
          if (!(child instanceof THREE.Mesh)) return;
          this.forEachMaterial(child.material, (material) => {
            if ('color' in material && material.color instanceof THREE.Color) material.color.lerp(tint, 0.2);
          });
        });
        root.add(model);
        const engine = new THREE.Mesh(new THREE.SphereGeometry(1.15, 10, 8), new THREE.MeshBasicMaterial({ color: 0xffa742, transparent: true, opacity: 0.48, blending: THREE.AdditiveBlending, depthWrite: false }));
        engine.position.set(0, -0.45, 8.8);
        root.add(engine);
        const createRemoteExhaust = (): { core: THREE.Sprite; glow: THREE.Sprite } => {
          const core = new THREE.Sprite(new THREE.SpriteMaterial({
            map: this.getSoftCircleTexture('flame'),
            color: 0xffa742,
            transparent: true,
            opacity: 0.74,
            depthWrite: false,
            depthTest: false,
            blending: THREE.AdditiveBlending,
          }));
          const glow = new THREE.Sprite(new THREE.SpriteMaterial({
            map: this.getSoftCircleTexture('flame'),
            color: 0xffc267,
            transparent: true,
            opacity: 0.36,
            depthWrite: false,
            depthTest: false,
            blending: THREE.AdditiveBlending,
          }));
          return { core, glow };
        };
        const leftExhaust = createRemoteExhaust();
        const rightExhaust = createRemoteExhaust();
        const modelScale = 1.3;
        const exhaustLayout = this.getScaledExhaustLayout('j15', modelScale);
        leftExhaust.core.position.copy(exhaustLayout.left);
        rightExhaust.core.position.copy(exhaustLayout.right);
        leftExhaust.glow.position.copy(exhaustLayout.leftGlow);
        rightExhaust.glow.position.copy(exhaustLayout.rightGlow);
        root.add(leftExhaust.core, rightExhaust.core, leftExhaust.glow, rightExhaust.glow);
        const label = this.createNameSprite(player.name);
        label.position.set(0, 18, 0);
        root.add(label);
        this.scene.add(root);
        this.remotePlayers.set(player.id, {
          root,
          modelScale,
          engine,
          afterburnerLeftCore: leftExhaust.core,
          afterburnerRightCore: rightExhaust.core,
          afterburnerLeftGlow: leftExhaust.glow,
          afterburnerRightGlow: rightExhaust.glow,
          label,
        });
      }
      const visual = this.remotePlayers.get(player.id);
      if (!visual) continue;
      visual.root.position.copy(player.position);
      visual.root.quaternion.copy(player.quaternion);
      const exhaustStrength = 0.66 + player.throttle * 0.88;
      const exhaustLayout = this.getScaledExhaustLayout('j15', visual.modelScale);
      visual.engine.scale.setScalar(0.42 + player.throttle * 0.28);
      (visual.engine.material as THREE.MeshBasicMaterial).opacity = 0;
      visual.afterburnerLeftCore.position.copy(exhaustLayout.left);
      visual.afterburnerRightCore.position.copy(exhaustLayout.right);
      visual.afterburnerLeftGlow.position.copy(exhaustLayout.leftGlow);
      visual.afterburnerRightGlow.position.copy(exhaustLayout.rightGlow);
      for (const core of [visual.afterburnerLeftCore, visual.afterburnerRightCore]) {
        core.scale.setScalar(exhaustLayout.coreBase + player.throttle * 0.36 * visual.modelScale);
        core.position.z += exhaustStrength * 0.14 * visual.modelScale;
        (core.material as THREE.SpriteMaterial).opacity = 0;
      }
      for (const glow of [visual.afterburnerLeftGlow, visual.afterburnerRightGlow]) {
        glow.scale.setScalar(exhaustLayout.glowBase + exhaustStrength * 0.44 * visual.modelScale);
        glow.position.z += exhaustStrength * 0.2 * visual.modelScale;
        (glow.material as THREE.SpriteMaterial).opacity = 0;
      }
      visual.label.quaternion.copy(this.camera.quaternion);
    }

    for (const [id, visual] of this.remotePlayers) {
      if (active.has(id)) continue;
      this.scene.remove(visual.root);
      this.remotePlayers.delete(id);
    }
  }

  syncRemoteMissiles(missiles: MissileRenderable[]): void {
    this.syncMissileSet(missiles, this.remoteMissiles);
  }

  projectWorldToScreen(position: THREE.Vector3): ScreenPoint {
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    const projected = position.clone().project(this.camera);
    return {
      x: (projected.x * 0.5 + 0.5) * width,
      y: (-projected.y * 0.5 + 0.5) * height,
      visible: projected.z > -1 && projected.z < 1,
    };
  }

  applyCameraLook(yawDelta: number, pitchDelta: number): void {
    this.cameraOrbitYaw -= yawDelta;
    this.cameraOrbitPitch = THREE.MathUtils.clamp(this.cameraOrbitPitch - pitchDelta, -0.42, 0.72);
  }

  applyCameraZoom(delta: number): void {
    if (Math.abs(delta) < 0.001) return;
    this.cameraZoomTarget = THREE.MathUtils.clamp(this.cameraZoomTarget - delta * 18, -34, 96);
  }

  setAutoTrackTarget(target: THREE.Vector3 | null): void {
    this.autoTrackTarget = target ? target.clone() : null;
  }

  private getExhaustLayout(aircraftId: string): {
    left: THREE.Vector3;
    right: THREE.Vector3;
    leftGlow: THREE.Vector3;
    rightGlow: THREE.Vector3;
    coreBase: number;
    glowBase: number;
  } {
    if (aircraftId === 'fa18f') {
      return {
        left: new THREE.Vector3(-3.9, -0.15, 13.8),
        right: new THREE.Vector3(3.9, -0.15, 13.8),
        leftGlow: new THREE.Vector3(-3.9, -0.15, 16.1),
        rightGlow: new THREE.Vector3(3.9, -0.15, 16.1),
        coreBase: 3.0,
        glowBase: 5.8,
      };
    }
    return {
      left: new THREE.Vector3(-0.55, -0.08, 7.95),
      right: new THREE.Vector3(1.35, -0.08, 7.95),
      leftGlow: new THREE.Vector3(-0.55, -0.08, 8.1),
      rightGlow: new THREE.Vector3(1.35, -0.08, 8.1),
      coreBase: 0.16,
      glowBase: 0.26,
    };
  }

  private getScaledExhaustLayout(aircraftId: string, scale: number): {
    left: THREE.Vector3;
    right: THREE.Vector3;
    leftGlow: THREE.Vector3;
    rightGlow: THREE.Vector3;
    coreBase: number;
    glowBase: number;
  } {
    const layout = this.getExhaustLayout(aircraftId);
    return {
      left: layout.left.clone().multiplyScalar(scale),
      right: layout.right.clone().multiplyScalar(scale),
      leftGlow: layout.leftGlow.clone().multiplyScalar(scale),
      rightGlow: layout.rightGlow.clone().multiplyScalar(scale),
      coreBase: layout.coreBase * scale,
      glowBase: layout.glowBase * scale,
    };
  }

  private getSoftCircleTexture(kind: 'flame' | 'smoke'): THREE.CanvasTexture {
    const cached = kind === 'flame' ? this.flameTexture : this.smokeTexture;
    if (cached) return cached;
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext('2d')!;
    const gradient = ctx.createRadialGradient(64, 64, 2, 64, 64, 62);
    if (kind === 'flame') {
      gradient.addColorStop(0, 'rgba(255,255,255,1)');
      gradient.addColorStop(0.18, 'rgba(255,236,150,0.96)');
      gradient.addColorStop(0.46, 'rgba(255,154,48,0.68)');
      gradient.addColorStop(1, 'rgba(255,68,16,0)');
    } else {
      gradient.addColorStop(0, 'rgba(20,20,20,0.72)');
      gradient.addColorStop(0.44, 'rgba(34,34,34,0.38)');
      gradient.addColorStop(1, 'rgba(34,34,34,0)');
    }
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.needsUpdate = true;
    if (kind === 'flame') this.flameTexture = texture;
    else this.smokeTexture = texture;
    return texture;
  }

  private setupScene(): void {
    this.scene.background = new THREE.Color(0x9ed9ea);
    this.scene.fog = new THREE.FogExp2(0x9ed9ea, 0.00019);

    const hemi = new THREE.HemisphereLight(0xdff8ff, 0x31424a, 1.35);
    this.scene.add(hemi);

    const sun = new THREE.DirectionalLight(0xfff0cc, 3.1);
    sun.position.set(-900, 1600, 600);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.near = 10;
    sun.shadow.camera.far = 4200;
    sun.shadow.camera.left = -1800;
    sun.shadow.camera.right = 1800;
    sun.shadow.camera.top = 1800;
    sun.shadow.camera.bottom = -1800;
    this.scene.add(sun);

    this.createOcean();
    this.createCarrierGroup();
    this.createClouds();
    this.createMountains();
    this.createBoundaryRing();
  }

  private createOcean(): void {
    const ocean = new THREE.Mesh(
      new THREE.PlaneGeometry(12000, 12000, 1, 1),
      new THREE.MeshStandardMaterial({
        color: 0x166978,
        roughness: 0.82,
        metalness: 0.08,
      }),
    );
    ocean.rotation.x = -Math.PI / 2;
    ocean.receiveShadow = true;
    this.scene.add(ocean);

    const grid = new THREE.GridHelper(8200, 82, 0xbcefff, 0x6fb5c4);
    grid.position.y = 1.2;
    const material = grid.material as THREE.Material | THREE.Material[];
    this.forEachMaterial(material, (item) => {
      item.transparent = true;
      item.opacity = 0.16;
      item.depthWrite = false;
    });
    this.scene.add(grid);
  }

  private createCarrierGroup(): void {
    const carrier = new THREE.Group();
    carrier.position.copy(this.simulation.basePosition);
    carrier.rotation.y = -0.16;
    this.loadCarrierModelInto(carrier);

    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(248, 2, 8, 96),
      new THREE.MeshBasicMaterial({ color: 0x74f29a, transparent: true, opacity: 0.32 }),
    );
    ring.rotation.x = Math.PI / 2;
    ring.position.y = 8;
    carrier.add(ring);
    this.scene.add(carrier);
    this.carrierGroup = carrier;
  }

  private async loadCarrierModelInto(carrier: THREE.Group): Promise<void> {
    try {
      const gltf = await this.gltfLoader.loadAsync('/assets/carrier/qe/scene.gltf');
      const model = gltf.scene.clone(true);
      model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.castShadow = true;
          child.receiveShadow = true;
          child.material = this.cloneMaterial(child.material);
        }
      });
      const box = new THREE.Box3().setFromObject(model);
      const size = new THREE.Vector3();
      box.getSize(size);
      const dominantLength = Math.max(size.x, size.z) || 1;
      const scale = 420 / dominantLength;
      model.scale.setScalar(scale);
      model.updateMatrixWorld(true);
      const scaledBox = new THREE.Box3().setFromObject(model);
      const scaledSize = new THREE.Vector3();
      scaledBox.getSize(scaledSize);
      if (scaledSize.x > scaledSize.z) model.rotation.y = Math.PI / 2;
      model.updateMatrixWorld(true);
      const fitBox = new THREE.Box3().setFromObject(model);
      const center = new THREE.Vector3();
      fitBox.getCenter(center);
      model.position.sub(center);
      model.position.y -= fitBox.min.y;
      model.position.y += 5.8;
      model.position.z += 8;
      model.updateMatrixWorld(true);
      carrier.add(model);
    } catch (error) {
      console.warn('Failed to load Queen Elizabeth carrier model, using procedural fallback.', error);
      this.addProceduralCarrierFallback(carrier);
    }
  }

  private addProceduralCarrierFallback(carrier: THREE.Group): void {
    const hull = new THREE.Mesh(
      new THREE.BoxGeometry(104, 22, 388),
      new THREE.MeshStandardMaterial({ color: 0x2d3438, roughness: 0.68, metalness: 0.25 }),
    );
    hull.castShadow = true;
    hull.receiveShadow = true;
    carrier.add(hull);

    const deck = new THREE.Mesh(
      new THREE.BoxGeometry(128, 5, 420),
      new THREE.MeshStandardMaterial({ color: 0x59646a, roughness: 0.58, metalness: 0.18 }),
    );
    deck.position.set(-6, 14, 0);
    deck.castShadow = true;
    deck.receiveShadow = true;
    carrier.add(deck);
  }

  private createClouds(): void {
    const cloudMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.56,
      roughness: 1,
      depthWrite: false,
    });
    const cloudGeometry = new THREE.SphereGeometry(1, 12, 8);
    for (let i = 0; i < 58; i += 1) {
      const cloud = new THREE.Group();
      const puffCount = 3 + Math.floor(Math.random() * 5);
      for (let j = 0; j < puffCount; j += 1) {
        const puff = new THREE.Mesh(cloudGeometry, cloudMaterial);
        puff.position.set(randomBetween(-32, 32), randomBetween(-8, 8), randomBetween(-18, 18));
        puff.scale.set(randomBetween(28, 64), randomBetween(10, 22), randomBetween(18, 42));
        cloud.add(puff);
      }
      const angle = randomBetween(0, Math.PI * 2);
      const radius = randomBetween(1100, 4700);
      cloud.position.set(Math.cos(angle) * radius, randomBetween(760, 1500), Math.sin(angle) * radius);
      this.scene.add(cloud);
    }
  }

  private createMountains(): void {
    const material = new THREE.MeshStandardMaterial({ color: 0x49615d, roughness: 0.92 });
    for (let i = 0; i < 36; i += 1) {
      const angle = (i / 36) * Math.PI * 2 + randomBetween(-0.04, 0.04);
      const radius = randomBetween(4300, 5600);
      const height = randomBetween(180, 620);
      const mountain = new THREE.Mesh(new THREE.ConeGeometry(randomBetween(180, 460), height, 5), material);
      mountain.position.set(Math.cos(angle) * radius, height * 0.5 - 8, Math.sin(angle) * radius);
      mountain.rotation.y = randomBetween(0, Math.PI);
      mountain.castShadow = true;
      mountain.receiveShadow = true;
      this.scene.add(mountain);
    }
  }

  private createBoundaryRing(): void {
    const geometry = new THREE.TorusGeometry(WORLD_RADIUS, 3, 8, 192);
    const material = new THREE.MeshBasicMaterial({
      color: 0xffc65a,
      transparent: true,
      opacity: 0.22,
      depthWrite: false,
    });
    const ring = new THREE.Mesh(geometry, material);
    ring.rotation.x = Math.PI / 2;
    ring.position.copy(this.simulation.basePosition);
    ring.position.y = 8;
    this.scene.add(ring);
  }

  private async loadAircraftTemplate(aircraftId: string): Promise<THREE.Object3D> {
    const cached = this.aircraftTemplates.get(aircraftId);
    if (cached) return cached;
    try {
      const normalized = await this.loadAndNormalizeAircraft(aircraftId);
      this.aircraftTemplates.set(aircraftId, normalized);
      return normalized;
    } catch (error) {
      console.warn('Failed to load aircraft model, using procedural fallback.', error);
      const fallback = this.createFallbackAircraft(aircraftId === 'fa18f' ? 0xc9d5dd : 0x8db7d9);
      this.aircraftTemplates.set(aircraftId, fallback);
      return fallback;
    }
  }

  private async loadAndNormalizeAircraft(aircraftId: string): Promise<THREE.Object3D> {
    if (aircraftId === 'fa18f') {
      const fbx = await this.fbxLoader.loadAsync('/assets/aircraft/fa18f/fa18f.fbx');
      return this.normalizeAircraftModel(fbx, aircraftId);
    }
    const source = aircraftId === 'j15' ? '/assets/aircraft/j15/scene.gltf' : '/assets/aircraft/j15/scene.gltf';
    const gltf = await this.gltfLoader.loadAsync(source);
    return this.normalizeAircraftModel(gltf.scene, aircraftId);
  }

  private normalizeAircraftModel(source: THREE.Object3D, aircraftId: string): THREE.Object3D {
    const template = source.clone(true);
    const disposable: THREE.Object3D[] = [];
    template.traverse((child) => {
      if (child instanceof THREE.Camera || child instanceof THREE.Light) {
        disposable.push(child);
        return;
      }
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        child.material = this.cloneMaterial(child.material);
        this.forEachMaterial(child.material, (material) => {
          if ('roughness' in material) {
            material.roughness = Math.min((material.roughness as number | undefined) ?? 0.82, 0.86);
          }
        });
      }
    });
    for (const child of disposable) child.parent?.remove(child);

    const box = new THREE.Box3().setFromObject(template);
    const size = new THREE.Vector3();
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const visualSize = aircraftId === 'fa18f' ? 18.6 : 19.6;
    template.scale.setScalar(visualSize / maxDim);
    template.rotation.set(0, aircraftId === 'fa18f' ? Math.PI : 0, aircraftId === 'j15' ? -0.08 : 0);
    template.updateMatrixWorld(true);

    const fitBox = new THREE.Box3().setFromObject(template);
    const center = new THREE.Vector3();
    fitBox.getCenter(center);
    template.position.sub(center);
    template.updateMatrixWorld(true);
    return template;
  }

  private syncAircraft(): void {
    const activeIds = new Set<string>([this.simulation.player.id]);
    this.ensureAircraft(this.simulation.player);
    this.updateAircraftVisual(this.simulation.player);

    for (const enemy of this.simulation.enemies.values()) {
      activeIds.add(enemy.id);
      this.ensureAircraft(enemy);
      this.updateAircraftVisual(enemy);
    }

    for (const [id, visual] of this.aircraft) {
      if (activeIds.has(id)) continue;
      this.scene.remove(visual.root);
      this.aircraft.delete(id);
    }
  }

  private ensureAircraft(aircraft: AircraftState): void {
    if (this.aircraft.has(aircraft.id)) return;
    const root = new THREE.Group();
    const aircraftId = aircraft.id === this.simulation.player.id ? this.selectedAircraftId : 'j15';
    const modelScale = aircraft.faction === 'player' ? 1.3 : 1;
    const model = this.createAircraftModel(aircraft.faction, aircraftId);
    root.add(model);

    const engine = new THREE.Mesh(
      new THREE.SphereGeometry(1.2, 12, 8),
      new THREE.MeshBasicMaterial({
        color: aircraft.faction === 'player' ? 0xffa742 : 0xff7848,
        transparent: true,
        opacity: 0.5,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    const initialExhaustLayout = this.getScaledExhaustLayout(aircraftId, modelScale);
    engine.position.set(0, initialExhaustLayout.left.y, initialExhaustLayout.left.z + 0.2);
    root.add(engine);
    const createExhaust = (): { core: THREE.Sprite; glow: THREE.Sprite } => {
      const core = new THREE.Sprite(new THREE.SpriteMaterial({
        map: this.getSoftCircleTexture('flame'),
        color: aircraft.faction === 'player' ? 0xffa742 : 0xff8f42,
        transparent: true,
        opacity: 0.78,
        depthWrite: false,
        depthTest: false,
        blending: THREE.AdditiveBlending,
      }));
      const glow = new THREE.Sprite(new THREE.SpriteMaterial({
        map: this.getSoftCircleTexture('flame'),
        color: aircraft.faction === 'player' ? 0xffc267 : 0xffb05c,
        transparent: true,
        opacity: 0.34,
        depthWrite: false,
        depthTest: false,
        blending: THREE.AdditiveBlending,
      }));
      return { core, glow };
    };
    const leftExhaust = createExhaust();
    const rightExhaust = createExhaust();
    const exhaustLayout = this.getScaledExhaustLayout(aircraftId, modelScale);
    leftExhaust.core.position.copy(exhaustLayout.left);
    rightExhaust.core.position.copy(exhaustLayout.right);
    leftExhaust.glow.position.copy(exhaustLayout.leftGlow);
    rightExhaust.glow.position.copy(exhaustLayout.rightGlow);
    root.add(leftExhaust.core, rightExhaust.core, leftExhaust.glow, rightExhaust.glow);
    const smokePrimary = new THREE.Sprite(new THREE.SpriteMaterial({
      map: this.getSoftCircleTexture('smoke'),
      color: 0x202020,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
    }));
    const smokeSecondary = new THREE.Sprite(new THREE.SpriteMaterial({
      map: this.getSoftCircleTexture('smoke'),
      color: 0x3a3a3a,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
    }));
    smokePrimary.position.set(0, 3.2, 18);
    smokeSecondary.position.set(0, 5.8, 23);
    smokePrimary.scale.set(10, 10, 1);
    smokeSecondary.scale.set(14, 14, 1);
    root.add(smokePrimary, smokeSecondary);

    const healthBack = new THREE.Mesh(
      new THREE.PlaneGeometry(26, 2.2),
      new THREE.MeshBasicMaterial({ color: 0x16080a, transparent: true, opacity: 0.7, depthTest: false }),
    );
    const healthFill = new THREE.Mesh(
      new THREE.PlaneGeometry(24, 1.2),
      new THREE.MeshBasicMaterial({ color: aircraft.faction === 'player' ? 0x74f29a : 0xff5c66, depthTest: false }),
    );
    healthBack.position.set(0, 15, 0);
    healthFill.position.set(0, 15, 0.1);
    healthBack.visible = aircraft.faction === 'enemy';
    healthFill.visible = aircraft.faction === 'enemy';
    root.add(healthBack, healthFill);

    const vaporMaterial = new THREE.SpriteMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
    });
    const vaporLeft = new THREE.Sprite(vaporMaterial.clone());
    const vaporRight = new THREE.Sprite(vaporMaterial.clone());
    vaporLeft.position.set(-7.8, 0.8, 1.8);
    vaporRight.position.set(7.8, 0.8, 1.8);
    vaporLeft.scale.set(8, 3.6, 1);
    vaporRight.scale.set(8, 3.6, 1);
    root.add(vaporLeft, vaporRight);

    this.scene.add(root);
    this.aircraft.set(aircraft.id, {
      root,
      aircraftId,
      modelScale,
      engine,
      afterburnerLeftCore: leftExhaust.core,
      afterburnerRightCore: rightExhaust.core,
      afterburnerLeftGlow: leftExhaust.glow,
      afterburnerRightGlow: rightExhaust.glow,
      smokePrimary,
      smokeSecondary,
      healthBack,
      healthFill,
      vaporLeft,
      vaporRight,
      lastForward: forwardOf(aircraft.quaternion, new THREE.Vector3()),
      vaporStrength: 0,
    });
  }

  private createAircraftModel(faction: 'player' | 'enemy', aircraftId: string): THREE.Object3D {
    const template = this.aircraftTemplates.get(aircraftId)
      ?? this.aircraftTemplate
      ?? this.createFallbackAircraft(aircraftId === 'fa18f' ? 0xc9d5dd : 0x8db7d9);
    const clone = cloneSkeleton(template);
    clone.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      child.material = this.cloneMaterial(child.material);
      this.forEachMaterial(child.material, (material) => {
        if ('color' in material && material.color instanceof THREE.Color) {
          if (faction === 'enemy') material.color.lerp(new THREE.Color(0xff4c4c), 0.42);
          if (faction === 'player') material.color.lerp(new THREE.Color(0x9defff), 0.12);
        }
        if ('emissive' in material && material.emissive instanceof THREE.Color && faction === 'enemy') {
          material.emissive.setHex(0x220604);
        }
      });
    });
    if (faction === 'player') clone.scale.multiplyScalar(1.3);
    return clone;
  }

  private updateAircraftVisual(aircraft: AircraftState): void {
    const visual = this.aircraft.get(aircraft.id);
    if (!visual) return;
    visual.root.position.copy(aircraft.position);
    visual.root.quaternion.copy(aircraft.quaternion);
    const engineScale = aircraft.faction === 'player'
      ? 0.42 + aircraft.throttle * 0.26 + (aircraft.afterburnerEnergy < 0.98 ? 0.12 : 0)
      : 0.4 + aircraft.throttle * 0.22;
    visual.engine.scale.setScalar(engineScale);
    (visual.engine.material as THREE.MeshBasicMaterial).opacity = visual.aircraftId === 'j15' ? 0 : 0.42;
    const afterburnerBoost = aircraft.faction === 'player'
      ? Math.max(0, 1 - aircraft.afterburnerEnergy)
      : Math.max(0, aircraft.throttle - 0.82) * 2.8;
    const exhaustLength = aircraft.faction === 'player'
      ? 0.85 + aircraft.throttle * 0.92 + afterburnerBoost * 1.25
      : 0.78 + aircraft.throttle * 0.62;
    const exhaustLayout = this.getScaledExhaustLayout(visual.aircraftId, visual.modelScale);
    visual.engine.position.set(0, exhaustLayout.left.y, exhaustLayout.left.z + 0.2);
    const j15Exhaust = visual.aircraftId === 'j15';
    visual.afterburnerLeftCore.position.copy(exhaustLayout.left);
    visual.afterburnerRightCore.position.copy(exhaustLayout.right);
    visual.afterburnerLeftGlow.position.copy(exhaustLayout.leftGlow);
    visual.afterburnerRightGlow.position.copy(exhaustLayout.rightGlow);
    for (const core of [visual.afterburnerLeftCore, visual.afterburnerRightCore]) {
      core.scale.setScalar(exhaustLayout.coreBase + exhaustLength * (j15Exhaust ? 0.28 : 1.75) * visual.modelScale + afterburnerBoost * (j15Exhaust ? 0.34 : 1.9) * visual.modelScale);
      core.position.z += exhaustLength * (j15Exhaust ? 0.12 : 1.45) * visual.modelScale;
      (core.material as THREE.SpriteMaterial).opacity = j15Exhaust ? 0 : 0.34 + aircraft.throttle * 0.28 + afterburnerBoost * 0.32;
    }
    for (const glow of [visual.afterburnerLeftGlow, visual.afterburnerRightGlow]) {
      glow.scale.setScalar(exhaustLayout.glowBase + exhaustLength * (j15Exhaust ? 0.36 : 2.9) * visual.modelScale + afterburnerBoost * (j15Exhaust ? 0.44 : 2.6) * visual.modelScale);
      glow.position.z += exhaustLength * (j15Exhaust ? 0.18 : 1.9) * visual.modelScale;
      (glow.material as THREE.SpriteMaterial).opacity = j15Exhaust ? 0 : 0.15 + aircraft.throttle * 0.18 + afterburnerBoost * 0.3;
    }
    const damageRatio = 1 - aircraft.health / Math.max(aircraft.maxHealth, 1);
    const smokeLevel = THREE.MathUtils.clamp((damageRatio - 0.42) / 0.45, 0, 1);
    visual.smokePrimary.position.z = 17 + smokeLevel * 4;
    visual.smokeSecondary.position.z = 21 + smokeLevel * 7;
    visual.smokePrimary.position.y = 3.2 + smokeLevel * 1.4;
    visual.smokeSecondary.position.y = 5.8 + smokeLevel * 2.6;
    visual.smokePrimary.scale.setScalar(8 + smokeLevel * 12);
    visual.smokeSecondary.scale.setScalar(12 + smokeLevel * 18);
    (visual.smokePrimary.material as THREE.SpriteMaterial).opacity = smokeLevel * 0.44;
    (visual.smokeSecondary.material as THREE.SpriteMaterial).opacity = smokeLevel * 0.32;

    const forward = forwardOf(aircraft.quaternion, new THREE.Vector3());
    const turnAngle = THREE.MathUtils.clamp(visual.lastForward.angleTo(forward), 0, Math.PI * 0.4);
    const speedFactor = THREE.MathUtils.clamp((aircraft.speed - 80) / 120, 0, 1);
    const maneuverFactor = THREE.MathUtils.clamp(turnAngle * 9.5, 0, 1);
    const targetVapor = maneuverFactor * speedFactor;
    visual.vaporStrength = THREE.MathUtils.lerp(visual.vaporStrength, targetVapor, 0.18);
    visual.lastForward.copy(forward);

    const vaporOpacity = visual.vaporStrength * 0.48;
    const vaporLength = 8 + visual.vaporStrength * 12;
    const vaporHeight = 3.6 + visual.vaporStrength * 3.8;
    const vaporOffsetZ = 1.8 - visual.vaporStrength * 3.2;
    visual.vaporLeft.position.z = vaporOffsetZ;
    visual.vaporRight.position.z = vaporOffsetZ;
    visual.vaporLeft.scale.set(vaporLength, vaporHeight, 1);
    visual.vaporRight.scale.set(vaporLength, vaporHeight, 1);
    (visual.vaporLeft.material as THREE.SpriteMaterial).opacity = vaporOpacity;
    (visual.vaporRight.material as THREE.SpriteMaterial).opacity = vaporOpacity;

    if (aircraft.faction === 'enemy') {
      visual.healthBack.quaternion.copy(this.camera.quaternion);
      visual.healthFill.quaternion.copy(this.camera.quaternion);
      const healthRatio = Math.max(0.02, aircraft.health / aircraft.maxHealth);
      visual.healthFill.scale.x = healthRatio;
      visual.healthFill.position.x = -(1 - healthRatio) * 12;
    }
  }

  private syncMissiles(): void {
    this.syncMissileSet([...this.simulation.missiles.values()], this.missiles);
  }

  private syncMissileSet(missiles: MissileRenderable[], store: Map<string, MissileVisual>): void {
    const activeIds = new Set<string>();
    for (const missile of missiles) {
      activeIds.add(missile.id);
      this.ensureMissile(missile, store);
      this.updateMissileVisual(missile, store);
    }

    for (const [id, visual] of store) {
      if (activeIds.has(id)) continue;
      this.scene.remove(visual.root);
      this.scene.remove(visual.line);
      store.delete(id);
    }
  }

  private ensureMissile(missile: MissileRenderable, store: Map<string, MissileVisual>): void {
    if (store.has(missile.id)) return;
    const root = new THREE.Group();
    const body = new THREE.Mesh(
      new THREE.ConeGeometry(1.4, 8, 12),
      new THREE.MeshStandardMaterial({
        color: missile.faction === 'player' ? 0xf4f7f8 : 0xffddd0,
        emissive: missile.faction === 'player' ? 0x16343b : 0x3b1108,
        roughness: 0.35,
        metalness: 0.3,
      }),
    );
    body.rotation.x = -Math.PI / 2;
    root.add(body);

    const flame = new THREE.Mesh(
      new THREE.SphereGeometry(1.8, 10, 8),
      new THREE.MeshBasicMaterial({
        color: 0xffdc6a,
        transparent: true,
        opacity: 0.78,
      }),
    );
    flame.position.z = 4.6;
    root.add(flame);

    const line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([missile.position.clone(), missile.position.clone()]),
      new THREE.LineBasicMaterial({
        color: missile.faction === 'player' ? 0xb8f6ff : 0xffad78,
        transparent: true,
        opacity: 0.42,
        depthWrite: false,
      }),
    );
    this.scene.add(root, line);
    store.set(missile.id, {
      root,
      line,
      points: [],
      flame,
      displayPosition: missile.position.clone(),
      displayDirection: missile.direction.clone(),
      targetPosition: missile.position.clone(),
      targetDirection: missile.direction.clone(),
      speed: missile.speed ?? 0,
      smooth: store === this.remoteMissiles,
      age: 0,
    });
  }

  private updateMissileVisual(missile: MissileRenderable, store: Map<string, MissileVisual>): void {
    const visual = store.get(missile.id);
    if (!visual) return;
    visual.age = missile.age ?? visual.age;
    if (!visual.smooth) {
      visual.displayPosition.copy(missile.position);
      visual.displayDirection.copy(missile.direction);
      visual.targetPosition.copy(missile.position);
      visual.targetDirection.copy(missile.direction);
      visual.speed = missile.speed ?? visual.speed;
      visual.root.position.copy(visual.displayPosition);
      visual.root.quaternion.copy(lookRotation(visual.displayDirection));
      visual.points.unshift(visual.displayPosition.clone());
    } else {
      visual.speed = missile.speed ?? visual.speed;
      visual.targetDirection.copy(missile.direction).normalize();
      visual.targetPosition.copy(missile.position).addScaledVector(visual.targetDirection, visual.speed * 0.18);
      if (visual.points.length === 0) {
        visual.displayPosition.copy(visual.targetPosition);
        visual.displayDirection.copy(visual.targetDirection);
        visual.root.position.copy(visual.displayPosition);
        visual.root.quaternion.copy(lookRotation(visual.displayDirection));
        visual.points.unshift(visual.displayPosition.clone());
      }
    }
    const flameMaterial = visual.flame.material as THREE.MeshBasicMaterial;
    const heatT = THREE.MathUtils.clamp(visual.age / 0.75, 0, 1);
    flameMaterial.color.set(heatT < 1
      ? new THREE.Color(0xffdc6a).lerp(new THREE.Color(0xf3fbff), heatT)
      : new THREE.Color(0xf3fbff));
    flameMaterial.opacity = 0.84 - heatT * 0.12;
    visual.points.length = Math.min(22, visual.points.length);
    visual.line.geometry.dispose();
    visual.line.geometry = new THREE.BufferGeometry().setFromPoints(visual.points);
  }

  private updateRemoteMissiles(dt: number): void {
    for (const visual of this.remoteMissiles.values()) {
      visual.age += dt;
      visual.displayPosition.lerp(visual.targetPosition, 1 - Math.exp(-12 * dt));
      visual.displayDirection.lerp(visual.targetDirection, 1 - Math.exp(-14 * dt)).normalize();
      visual.root.position.copy(visual.displayPosition);
      visual.root.quaternion.copy(lookRotation(visual.displayDirection));
      const flameMaterial = visual.flame.material as THREE.MeshBasicMaterial;
      const heatT = THREE.MathUtils.clamp(visual.age / 0.75, 0, 1);
      flameMaterial.color.set(heatT < 1
        ? new THREE.Color(0xffdc6a).lerp(new THREE.Color(0xf3fbff), heatT)
        : new THREE.Color(0xf3fbff));
      flameMaterial.opacity = 0.84 - heatT * 0.12;
      visual.points.unshift(visual.displayPosition.clone());
      visual.points.length = Math.min(22, visual.points.length);
      visual.line.geometry.dispose();
      visual.line.geometry = new THREE.BufferGeometry().setFromPoints(visual.points);
    }
  }

  private syncFlares(): void {
    const activeIds = new Set<string>();
    for (const flare of this.simulation.flares.values()) {
      activeIds.add(flare.id);
      if (!this.flares.has(flare.id)) {
        const mesh = new THREE.Mesh(
          new THREE.SphereGeometry(3.2, 12, 8),
          new THREE.MeshBasicMaterial({ color: 0xffc65a, transparent: true, opacity: 0.95 }),
        );
        const light = new THREE.PointLight(0xff9f45, 24, 280);
        this.scene.add(mesh, light);
        this.flares.set(flare.id, { mesh, light });
      }
      const visual = this.flares.get(flare.id);
      if (visual) {
        visual.mesh.position.copy(flare.position);
        visual.mesh.scale.setScalar(0.8 + flare.heat * 1.8);
        visual.light.position.copy(flare.position);
        visual.light.intensity = flare.heat * 45;
      }
    }

    for (const [id, visual] of this.flares) {
      if (activeIds.has(id)) continue;
      this.scene.remove(visual.mesh, visual.light);
      this.flares.delete(id);
    }
  }

  private applyEvents(events: SimEvent[]): void {
    for (const event of events) {
      if (event.type === 'explosion') {
        const material = new THREE.MeshBasicMaterial({
          color: event.tone === 'large' ? 0xffc65a : 0x4de7ff,
          transparent: true,
          opacity: 0.72,
          depthWrite: false,
        });
        const mesh = new THREE.Mesh(new THREE.SphereGeometry(1, 24, 16), material);
        mesh.position.copy(event.position);
        this.scene.add(mesh);
        this.explosions.push({ mesh, age: 0, life: event.tone === 'large' ? 0.72 : 0.38, maxRadius: event.radius });
      }

      if (event.type === 'gun-tracer') {
        const line = new THREE.Line(
          new THREE.BufferGeometry().setFromPoints([event.from.clone(), event.to.clone()]),
          new THREE.LineBasicMaterial({
            color: event.faction === 'player' ? 0xfff5b8 : 0xff635c,
            transparent: true,
            opacity: event.hit ? 0.95 : 0.58,
            depthWrite: false,
          }),
        );
        this.scene.add(line);
        this.tracers.push({ line, age: 0, life: 0.16 });
      }

      if (event.type === 'missile-tracer') {
        const line = new THREE.Line(
          new THREE.BufferGeometry().setFromPoints([event.from.clone(), event.to.clone()]),
          new THREE.LineBasicMaterial({
            color: event.faction === 'player' ? 0x78f6ff : 0xff9b6e,
            transparent: true,
            opacity: 0.92,
            depthWrite: false,
          }),
        );
        this.scene.add(line);
        this.tracers.push({ line, age: 0, life: 0.24 });
      }
    }
  }

  private updateExplosions(dt: number): void {
    for (let i = this.explosions.length - 1; i >= 0; i -= 1) {
      const explosion = this.explosions[i];
      explosion.age += dt;
      const t = explosion.age / explosion.life;
      const scale = explosion.maxRadius * Math.sin(Math.min(1, t) * Math.PI * 0.5);
      explosion.mesh.scale.setScalar(Math.max(0.01, scale));
      const material = explosion.mesh.material as THREE.MeshBasicMaterial;
      material.opacity = Math.max(0, 0.72 * (1 - t));
      if (explosion.age >= explosion.life) {
        this.scene.remove(explosion.mesh);
        explosion.mesh.geometry.dispose();
        material.dispose();
        this.explosions.splice(i, 1);
      }
    }
  }

  private updateTracers(dt: number): void {
    for (let i = this.tracers.length - 1; i >= 0; i -= 1) {
      const tracer = this.tracers[i];
      tracer.age += dt;
      const material = tracer.line.material as THREE.LineBasicMaterial;
      material.opacity = Math.max(0, 1 - tracer.age / tracer.life);
      if (tracer.age >= tracer.life) {
        this.scene.remove(tracer.line);
        tracer.line.geometry.dispose();
        material.dispose();
        this.tracers.splice(i, 1);
      }
    }
  }

  private updateCamera(dt: number): void {
    const player = this.simulation.player;
    const rawForward = forwardOf(player.quaternion, new THREE.Vector3());
    const flatForward = rawForward.clone();
    flatForward.y = 0;
    if (flatForward.lengthSq() < 0.0001) flatForward.set(0, 0, -1);
    flatForward.normalize();
    const speedBlend = THREE.MathUtils.clamp((player.speed - 120) / 180, 0, 1);
    this.cameraZoomOffset = THREE.MathUtils.damp(this.cameraZoomOffset, this.cameraZoomTarget, 8, dt);
    const distance = 96 + speedBlend * 34 + this.cameraZoomOffset;
    const desired = player.position.clone()
      .addScaledVector(flatForward, -distance)
      .addScaledVector(WORLD_UP, 28 + speedBlend * 8);
    this.camera.position.lerp(desired, 1 - Math.exp(-5.5 * dt));
    let lookTarget = player.position.clone()
      .addScaledVector(flatForward, 24)
      .addScaledVector(WORLD_UP, 8);
    if (this.autoTrackTarget) {
      const focus = this.autoTrackTarget.clone().sub(player.position);
      if (focus.lengthSq() > 1) {
        lookTarget = player.position.clone()
          .addScaledVector(focus.normalize(), 32)
          .addScaledVector(WORLD_UP, 4);
      }
    }
    this.camera.lookAt(lookTarget);
  }

  private createFallbackAircraft(color: number): THREE.Object3D {
    const group = new THREE.Group();
    const bodyMaterial = new THREE.MeshStandardMaterial({
      color,
      roughness: 0.42,
      metalness: 0.22,
    });
    const wingMaterial = new THREE.MeshStandardMaterial({
      color: 0x303a40,
      roughness: 0.5,
      metalness: 0.18,
    });

    const fuselage = new THREE.Mesh(new THREE.ConeGeometry(1.2, 9, 10), bodyMaterial);
    fuselage.rotation.x = -Math.PI / 2;
    group.add(fuselage);

    const wings = new THREE.Mesh(new THREE.BoxGeometry(13, 0.26, 2.6), wingMaterial);
    wings.position.z = 0.8;
    group.add(wings);

    const tail = new THREE.Mesh(new THREE.BoxGeometry(5.2, 0.22, 1.5), wingMaterial);
    tail.position.z = 4.4;
    group.add(tail);

    const fin = new THREE.Mesh(new THREE.BoxGeometry(0.28, 2.4, 1.5), wingMaterial);
    fin.position.set(0, 1.1, 4.2);
    group.add(fin);
    return group;
  }

  private decorateCarrierDeck(): void {
    if (!this.carrierGroup) return;
    for (const child of [...this.carrierGroup.children]) {
      if (child.userData.deckAircraft) this.carrierGroup.remove(child);
    }
    const j15Template = this.aircraftTemplates.get('j15');
    const fa18Template = this.aircraftTemplates.get('fa18f');
    if (!j15Template || !fa18Template) return;
    const parked = [
      { x: 50, y: 8.1, z: -132, yaw: Math.PI, scale: 0.82, model: j15Template },
      { x: 28, y: 8.1, z: -72, yaw: Math.PI, scale: 0.8, model: fa18Template },
      { x: 44, y: 8.1, z: -6, yaw: Math.PI, scale: 0.8, model: j15Template },
      { x: -30, y: 8.3, z: 118, yaw: Math.PI - 0.42, scale: 0.76, model: fa18Template },
      { x: -64, y: 8.3, z: 62, yaw: Math.PI - 0.42, scale: 0.76, model: j15Template },
    ];
    for (const slot of parked) {
      const clone = cloneSkeleton(slot.model);
      clone.scale.multiplyScalar(slot.scale);
      clone.position.set(slot.x, slot.y, slot.z);
      clone.rotation.y += slot.yaw;
      clone.userData.deckAircraft = true;
      clone.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      this.carrierGroup.add(clone);
    }
  }

  private createNameSprite(text: string): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = 'rgba(7, 17, 23, 0.76)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = 'rgba(125, 255, 168, 0.9)';
      ctx.strokeRect(1, 1, canvas.width - 2, canvas.height - 2);
      ctx.fillStyle = '#eafbf0';
      ctx.font = 'bold 28px "Segoe UI", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text.slice(0, 18), canvas.width / 2, canvas.height / 2);
    }
    const texture = new THREE.CanvasTexture(canvas);
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, depthWrite: false, depthTest: false }));
    sprite.scale.set(34, 8.5, 1);
    return sprite;
  }

  private cloneMaterial(material: THREE.Material | THREE.Material[]): THREE.Material | THREE.Material[] {
    if (Array.isArray(material)) return material.map((item) => item.clone());
    return material.clone();
  }

  private forEachMaterial(material: THREE.Material | THREE.Material[], apply: (material: THREE.Material) => void): void {
    if (Array.isArray(material)) {
      for (const item of material) apply(item);
      return;
    }
    apply(material);
  }

  private readonly resize = (): void => {
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
  };
}
